import React, { useState, useEffect } from 'react';
import 'moment/locale/it';
import Accordion, { AccordionStyles } from '../Accordion/Accordion';
import { TRANSLATIONS } from '../../../translations';
import BetaIcon, { IconSizes } from '../Icons/Icon';
import ContactForm from '../ContactForm';
import axios from 'axios';
import endpoints from '../../define/endpoints';
import { camelizeKeys } from 'humps';
import { BREAKPOINTS, returnHubspotContactUsFormId } from '../../../constants';
import { useMatchesMinWidth } from '../../hooks/useMatchesMinWidth';
import Button, { ButtonSizes, ButtonStyles } from '../Button/Button';

const FaqElement = ({
  faq,
  hubspotFormType = 'default',
  phoneLink,
  index,
  startTime,
  endTime,
  isPhoneActive,
  variant = 'default',
  idOrder = null,
  partnerInfo = null,
  canShowPartnerInfo = null,
}) => {
  const [openContactIndex, setOpenContactIndex] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [isCucaCurrentlyOpen, setIsCucaCurrentlyOpen] = useState(false);
  const [cucaSettingsLoaded, setCucaSettingsLoaded] = useState(false);
  const hubspotFormId = returnHubspotContactUsFormId(hubspotFormType);
  const isDeviceSmallerThanTablet = !useMatchesMinWidth(BREAKPOINTS.TABLET);
  const [showFreedomeOptions, setShowFreedomeOptions] = useState(false);
  const shouldShowPartnerContacts =
    canShowPartnerInfo && !!faq.show_partner_contacts && partnerInfo;

  const dividerElement = (
    <div
      style={{
        background: '#e4e8ee',
        height: '1px',
        width: '100%',
        margin: 'auto',
      }}
    ></div>
  );

  useEffect(() => {
    checkCucaStatus();
  }, []);

  const isCucaOpen = async () => {
    try {
      const currentTime = new Date();
      const currentHour = currentTime.getHours();
      const currentMinute = currentTime.getMinutes();
      const currentSeconds = currentTime.getSeconds();

      if (!Number(isPhoneActive)) {
        return false;
      }

      const startTimeParts = startTime.split(':').map(Number);
      const endTimeParts = endTime.split(':').map(Number);

      const [startHour, startMinute, startSeconds = 0] = startTimeParts;
      const [endHour, endMinute, endSeconds = 0] = endTimeParts;

      const currentTotalSeconds =
        currentHour * 3600 + currentMinute * 60 + currentSeconds;
      const startTotalSeconds =
        startHour * 3600 + startMinute * 60 + (startSeconds || 0);
      const endTotalSeconds =
        endHour * 3600 + endMinute * 60 + (endSeconds || 0);

      return (
        currentTotalSeconds >= startTotalSeconds &&
        currentTotalSeconds <= endTotalSeconds
      );
    } catch (error) {
      // console.error('Error checking CuCa opening hours:', error);
      return false;
    }
  };

  const checkCucaStatus = async () => {
    const isOpen = await isCucaOpen();
    setIsCucaCurrentlyOpen(isOpen);
    setCucaSettingsLoaded(true);
  };

  const CustomerSupportOptions = ({
    showContactForm,
    setShowContactForm,
    hubspotFormId,
    idOrder,
    cucaSettingsLoaded,
    isCucaCurrentlyOpen,
    phoneLink,
  }) => {
    return (
      <div className="cs-options d-flex flex-column items-start gap-s pt-s">
        <div
          onClick={() => window.toggleHubspotChat()}
          className="pointer d-flex items-center justify-between w-100%"
        >
          <div style={{ maxWidth: 'calc(100% - 16px)' }}>
            <div className="d-flex items-center gap-xs typography__weight--bold">
              <BetaIcon name="chat" size={IconSizes[16]} />
              {TRANSLATIONS.START_CHAT}
            </div>
            <div className="d-flex items-center gap-xs pt-xs">
              {TRANSLATIONS.CHAT_IN_REAL_TIME_WITH_OUR_EXPERTS}
            </div>
          </div>
          <BetaIcon name="chevronRight" size={IconSizes[12]} />
        </div>

        {dividerElement}

        <div className="d-flex flex-column w-100%">
          <div
            className="pointer"
            onClick={() => setShowContactForm(!showContactForm)}
          >
            <div className="d-flex items-center justify-between">
              <div style={{ maxWidth: 'calc(100% - 16px)' }}>
                <div className="d-flex items-center gap-xs typography__weight--bold">
                  <BetaIcon name="mail" size={IconSizes[16]} />
                  {TRANSLATIONS.SEND_AN_EMAIL}
                </div>
                <div className="d-flex items-center gap-xs pt-xs">
                  {TRANSLATIONS.WRITE_TO_OUR_TEAM_WE_ARE_HAPPY_TO_HELP_YOU}
                </div>
              </div>
              <BetaIcon
                name={showContactForm ? 'chevronDown' : 'chevronRight'}
                size={showContactForm ? IconSizes[20] : IconSizes[12]}
              />
            </div>
          </div>

          {showContactForm && (
            <div className="pt-m w-100%">
              <ContactForm
                role="customer"
                hubspotFormId={hubspotFormId}
                textAreaRows={7}
                idOrder={idOrder}
              />
            </div>
          )}
        </div>

        {dividerElement}

        {cucaSettingsLoaded && isCucaCurrentlyOpen && (
          <div className="w-100%">
            <a
              className="decoration-none pointer d-flex items-center justify-between"
              href={`tel:${phoneLink}`}
              data-gtm-item={phoneLink}
            >
              <div style={{ maxWidth: 'calc(100% - 16px)' }}>
                <div className="d-flex  items-center gap-xs typography__weight--bold">
                  <BetaIcon name="phone" size={IconSizes[16]} />
                  {TRANSLATIONS.CALL_CUSTOMER_SERVICE}
                </div>
                <div className="d-flex items-center gap-2xs pt-xs phone-link">
                  {TRANSLATIONS.WE_ARE_AVAILABLE_SEVEN_DAYS_A_WEEK}
                  <u>{phoneLink}</u>
                </div>
              </div>

              <BetaIcon name="chevronRight" size={IconSizes[12]} />
            </a>
          </div>
        )}
      </div>
    );
  };

  const ContactsOptions = ({ index, shouldShowPartnerContacts }) => {
    return (
      <>
        {openContactIndex !== index && (
          <div className="pt-xs pb-xs">
            {TRANSLATIONS.STILL_NEED_HELP}{' '}
            <button
              className="pointer"
              onClick={() => setOpenContactIndex(index)}
            >
              <u>{TRANSLATIONS.CLICK_HERE}</u>
            </button>
          </div>
        )}

        {openContactIndex === index && (
          <>
            {shouldShowPartnerContacts ? (
              <div>
                <div className="d-flex flex-column items-start gap-xs pt-s pb-s">
                  <div className="d-flex items-center gap-3xs typography__weight--bold">
                    <BetaIcon name="partner" size={IconSizes[16]} />
                    {TRANSLATIONS.CONTACT_THE_GUIDE}
                  </div>

                  {partnerInfo.name && (
                    <div className="d-flex gap-base">
                      {`${partnerInfo.firstName} ${partnerInfo.lastName}`}{' '}
                      <br />
                      {`${partnerInfo.name}`}
                    </div>
                  )}

                  {isDeviceSmallerThanTablet ? (
                    <div className="w-100% d-flex gap-xs">
                      {partnerInfo.phone && (
                        <Button
                          iconLeft={
                            <BetaIcon name="phone" size={IconSizes[16]} />
                          }
                          size={ButtonSizes.s}
                          style={ButtonStyles.empty}
                          label={TRANSLATIONS.CALL}
                          redirect={`tel:${partnerInfo.phone}`}
                        />
                      )}
                      {partnerInfo.email && (
                        <Button
                          iconLeft={
                            <BetaIcon name="mail" size={IconSizes[16]} />
                          }
                          size={ButtonSizes.s}
                          style={ButtonStyles.empty}
                          label={TRANSLATIONS.WRITE_EMAIL}
                          redirect={`mailto:${partnerInfo.email}`}
                        />
                      )}
                    </div>
                  ) : (
                    <>
                      {partnerInfo.phone && (
                        <div className="d-flex gap-base">
                          <span>{TRANSLATIONS.PHONE}:</span>
                          <a href={`tel:${partnerInfo.phone}`}>
                            {partnerInfo.phone}
                          </a>
                        </div>
                      )}

                      {partnerInfo.email && (
                        <div className="d-flex gap-base">
                          <span>
                            {TRANSLATIONS.EMAIL.charAt(0).toUpperCase() +
                              TRANSLATIONS.EMAIL.slice(1)}
                            :
                          </span>
                          <a href={`mailto:${partnerInfo.email}`}>
                            {partnerInfo.email}
                          </a>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {showFreedomeOptions ? (
                  <div className="w-100%">
                    {dividerElement}
                    <CustomerSupportOptions
                      showContactForm={showContactForm}
                      setShowContactForm={setShowContactForm}
                      hubspotFormId={hubspotFormId}
                      idOrder={idOrder}
                      cucaSettingsLoaded={cucaSettingsLoaded}
                      isCucaCurrentlyOpen={isCucaCurrentlyOpen}
                      phoneLink={phoneLink}
                    />
                  </div>
                ) : (
                  <>
                    {dividerElement}
                    <button
                      className="pt-s pb-2xs pointer text-underlined"
                      onClick={() =>
                        setShowFreedomeOptions(!showFreedomeOptions)
                      }
                    >
                      {TRANSLATIONS.VIEW_MORE_OPTIONS}
                    </button>
                  </>
                )}
              </div>
            ) : (
              <CustomerSupportOptions
                showContactForm={showContactForm}
                setShowContactForm={setShowContactForm}
                hubspotFormId={hubspotFormId}
                idOrder={idOrder}
                cucaSettingsLoaded={cucaSettingsLoaded}
                isCucaCurrentlyOpen={isCucaCurrentlyOpen}
                phoneLink={phoneLink}
              />
            )}
          </>
        )}
      </>
    );
  };

  return (
    <Accordion
      key={`faq-nested-${index}`}
      style={
        variant == 'search'
          ? AccordionStyles.faqSearch
          : AccordionStyles.faqSecondary
      }
      isInitiallyOpen={false}
      label={faq.question}
      trackClicks={true}
    >
      <div className="d-flex flex-column gap-xs">
        <div
          className="text-with-images typography__p5 typography__neutral text-left"
          dangerouslySetInnerHTML={{
            __html: faq.answer,
          }}
        />
        <ContactsOptions
          index={index}
          shouldShowPartnerContacts={shouldShowPartnerContacts}
        />
      </div>
    </Accordion>
  );
};

FaqElement.propTypes = {};

export default FaqElement;
